import { useCallback } from "react";
import type { AdminIdentity, AdminRole } from "@/api/types";
import { useAdminContext } from "@/hooks/use-admin-context";

/**
 * Frontend capabilities. They only decide what the console *offers*; the backend authorizes
 * every request again. Hiding a button is never a security control.
 */
export const CAPABILITIES = [
  "platform:view",
  "organizations:view",
  "organizations:update",
  "organizations:suspend",
  "stores:view",
  "stores:manage",
  "users:view",
  "users:manage",
  "tenant-data:view",
  /** Work a dealership's leads in the dealer app as yourself (SUPER_ADMIN, ADMIN, OPERATIONS). */
  "tenant-data:act",
  "ai:view",
  /** Edit the parts of Rogue's prompt admins control (SUPER_ADMIN, ADMIN). */
  "prompts:manage",
  "queues:view",
  "queues:manage",
  "integrations:view",
  "integrations:manage",
  "finance:view",
  "audit:view",
  "system:view",
  "system:manage",
  "staff:view",
  "staff:manage",
  /** Development tools (SMS simulator); the backend only serves them outside production. */
  "dev-tools:use",
] as const;
export type Capability = (typeof CAPABILITIES)[number];

const ALL = [...CAPABILITIES];
const VIEW_ALL: Capability[] = ALL.filter((c) => c.endsWith(":view"));

/**
 * Role → capabilities, used only when the backend doesn't send explicit permissions (it does:
 * GET /admin/v1/me). Mirrors rogue/backend/src/modules/admin/admin-permissions.ts.
 */
export const ROLE_CAPABILITIES: Record<AdminRole, Capability[]> = {
  SUPER_ADMIN: ALL,
  ADMIN: ALL.filter((c) => c !== "system:manage" && c !== "staff:manage"),
  SUPPORT: [...VIEW_ALL.filter((c) => c !== "finance:view"), "users:manage"],
  OPERATIONS: [...VIEW_ALL.filter((c) => c !== "finance:view"), "queues:manage", "integrations:manage", "system:manage", "dev-tools:use", "tenant-data:act"],
  FINANCE: ["platform:view", "organizations:view", "stores:view", "finance:view", "audit:view"],
  READ_ONLY: VIEW_ALL.filter((c) => c !== "finance:view"),
};

export function capabilitiesOf(identity: AdminIdentity | null | undefined): Set<Capability> {
  if (!identity) return new Set();
  if (identity.permissions) return new Set(identity.permissions.filter((p): p is Capability => (CAPABILITIES as readonly string[]).includes(p)));
  return new Set(identity.role ? ROLE_CAPABILITIES[identity.role] : []);
}

export function can(identity: AdminIdentity | null | undefined, capability: Capability): boolean {
  return capabilitiesOf(identity).has(capability);
}

/**
 * `can` gates actions (hidden unless the role grants them). `canView` gates navigation: while the
 * backend doesn't expose the admin role (roleKnown = false) every section stays visible and the
 * backend's answer on each request decides.
 */
export function usePermissions() {
  const { identity, roleKnown } = useAdminContext();
  return {
    can: useCallback((c: Capability) => can(identity, c), [identity]),
    canView: useCallback((c: Capability | undefined) => !c || !roleKnown || can(identity, c), [identity, roleKnown]),
  };
}
